import * as z from 'zod';

export const GetPermissionsRequest = /*#__PURE__*/ z.object({
  dataCollectionId: z
    .string()
    .describe('ID of the collection to retrieve permissions of.')
    .max(256),
});
export const GetPermissionsResponse = /*#__PURE__*/ z.object({
  dataPermissions: z
    .object({
      _id: z
        .string()
        .describe('ID of the collection for these permissions.')
        .max(255)
        .optional(),
      itemRead: z
        .enum([
          'ANYONE',
          'SITE_MEMBER',
          'SITE_MEMBER_AUTHOR',
          'CMS_EDITOR',
          'PRIVILEGED',
        ])
        .describe(
          'Which users can read items from the collection.\n\nLearn more about [item actions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#item-actions).'
        )
        .optional(),
      itemInsert: z
        .enum([
          'ANYONE',
          'SITE_MEMBER',
          'SITE_MEMBER_AUTHOR',
          'CMS_EDITOR',
          'PRIVILEGED',
        ])
        .describe(
          'Which users can create new items in the collection.\n\nLearn more about [item actions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#item-actions).'
        )
        .optional(),
      itemUpdate: z
        .enum([
          'ANYONE',
          'SITE_MEMBER',
          'SITE_MEMBER_AUTHOR',
          'CMS_EDITOR',
          'PRIVILEGED',
        ])
        .describe(
          'Which users can update existing items in the collection.\n\nLearn more about [item actions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#item-actions).'
        )
        .optional(),
      itemRemove: z
        .enum([
          'ANYONE',
          'SITE_MEMBER',
          'SITE_MEMBER_AUTHOR',
          'CMS_EDITOR',
          'PRIVILEGED',
        ])
        .describe(
          'Which users can remove items from the collection.\n\nLearn more about [item actions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#item-actions).'
        )
        .optional(),
      specialPermissions: z
        .array(
          z.intersection(
            z.object({
              _id: z
                .string()
                .describe('ID of the special permissions set.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              itemRead: z
                .enum(['UNSPECIFIED', 'ALLOWED'])
                .describe(
                  'Whether the specified users or roles can read items from the collection.\n\nWhen set to `ALLOWED`, users can read items from the collection. When set to `UNSPECIFIED`, [collection-level permissions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#collection-level-permissions) apply.\n\nLearn more about [item actions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#item-actions).'
                )
                .optional(),
              itemInsert: z
                .enum(['UNSPECIFIED', 'ALLOWED'])
                .describe(
                  'Whether the specified users or roles can create new items in the collection.\n\nWhen set to `ALLOWED`, users can create new items in the collection. When set to `UNSPECIFIED`, [collection-level permissions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#collection-level-permissions) apply.\n\nLearn more about [item actions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#item-actions).'
                )
                .optional(),
              itemUpdate: z
                .enum(['UNSPECIFIED', 'ALLOWED'])
                .describe(
                  'Whether the specified users or roles can update existing items in the collection.\n\nWhen set to `ALLOWED`, users can update existing items in the collection. When set to `UNSPECIFIED`, [collection-level permissions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#collection-level-permissions) apply.\n\nLearn more about [item actions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#item-actions).'
                )
                .optional(),
              itemRemove: z
                .enum(['UNSPECIFIED', 'ALLOWED'])
                .describe(
                  'Whether the specified users or roles can remove items from the collection.\n\nWhen set to `ALLOWED`, users can remove items from the collection. When set to `UNSPECIFIED`, [collection-level permissions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#collection-level-permissions) apply.\n\nLearn more about [item actions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#item-actions).'
                )
                .optional(),
            }),
            z.xor([
              z.object({
                userId: z.never().optional(),
                policyId: z.never().optional(),
              }),
              z.object({
                policyId: z.never().optional(),
                userId: z
                  .string()
                  .describe('ID of the user for these special permissions.')
                  .max(64),
              }),
              z.object({
                userId: z.never().optional(),
                policyId: z
                  .string()
                  .describe(
                    'ID of the [role](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#user-roles) for these special permissions.\n\nTo find the ID of a role, call [Get Roles Info](https://dev.wix.com/docs/rest/account-level/user-management/accounts/users/get-roles-info). Roles and their IDs are found in the `predefinedRoles` or the `customRoles` arrays in the method response.\n\nLearn more about [roles and permissions for site management](https://support.wix.com/en/article/roles-permissions-overview#roles-and-permissions).'
                  )
                  .max(64),
              }),
            ])
          )
        )
        .max(1000)
        .optional(),
    })
    .describe('Permissions of the specified collection.')
    .optional(),
});
export const UpdatePermissionsRequest = /*#__PURE__*/ z.object({
  dataPermissions: z
    .object({
      _id: z
        .string()
        .describe('ID of the collection for these permissions.')
        .max(255),
      itemRead: z.enum([
        'ANYONE',
        'SITE_MEMBER',
        'SITE_MEMBER_AUTHOR',
        'CMS_EDITOR',
        'PRIVILEGED',
      ]),
      itemInsert: z.enum([
        'ANYONE',
        'SITE_MEMBER',
        'SITE_MEMBER_AUTHOR',
        'CMS_EDITOR',
        'PRIVILEGED',
      ]),
      itemUpdate: z.enum([
        'ANYONE',
        'SITE_MEMBER',
        'SITE_MEMBER_AUTHOR',
        'CMS_EDITOR',
        'PRIVILEGED',
      ]),
      itemRemove: z.enum([
        'ANYONE',
        'SITE_MEMBER',
        'SITE_MEMBER_AUTHOR',
        'CMS_EDITOR',
        'PRIVILEGED',
      ]),
      specialPermissions: z
        .array(
          z.intersection(
            z.object({
              _id: z
                .string()
                .describe('ID of the special permissions set.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              itemRead: z.enum(['UNSPECIFIED', 'ALLOWED']).optional(),
              itemInsert: z.enum(['UNSPECIFIED', 'ALLOWED']).optional(),
              itemUpdate: z.enum(['UNSPECIFIED', 'ALLOWED']).optional(),
              itemRemove: z.enum(['UNSPECIFIED', 'ALLOWED']).optional(),
            }),
            z.xor([
              z.object({
                userId: z.never().optional(),
                policyId: z.never().optional(),
              }),
              z.object({
                policyId: z.never().optional(),
                userId: z
                  .string()
                  .describe('ID of the user for these special permissions.')
                  .max(64),
              }),
              z.object({
                userId: z.never().optional(),
                policyId: z
                  .string()
                  .describe(
                    'ID of the [role](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#user-roles) for these special permissions.\n\nTo find the ID of a role, call [Get Roles Info](https://dev.wix.com/docs/rest/account-level/user-management/accounts/users/get-roles-info). Roles and their IDs are found in the `predefinedRoles` or the `customRoles` arrays in the method response.\n\nLearn more about [roles and permissions for site management](https://support.wix.com/en/article/roles-permissions-overview#roles-and-permissions).'
                  )
                  .max(64),
              }),
            ])
          )
        )
        .max(1000)
        .optional(),
    })
    .describe('Permissions to update.'),
});
export const UpdatePermissionsResponse = /*#__PURE__*/ z.object({
  dataPermissions: z
    .object({
      _id: z
        .string()
        .describe('ID of the collection for these permissions.')
        .max(255)
        .optional(),
      itemRead: z
        .enum([
          'ANYONE',
          'SITE_MEMBER',
          'SITE_MEMBER_AUTHOR',
          'CMS_EDITOR',
          'PRIVILEGED',
        ])
        .describe(
          'Which users can read items from the collection.\n\nLearn more about [item actions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#item-actions).'
        )
        .optional(),
      itemInsert: z
        .enum([
          'ANYONE',
          'SITE_MEMBER',
          'SITE_MEMBER_AUTHOR',
          'CMS_EDITOR',
          'PRIVILEGED',
        ])
        .describe(
          'Which users can create new items in the collection.\n\nLearn more about [item actions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#item-actions).'
        )
        .optional(),
      itemUpdate: z
        .enum([
          'ANYONE',
          'SITE_MEMBER',
          'SITE_MEMBER_AUTHOR',
          'CMS_EDITOR',
          'PRIVILEGED',
        ])
        .describe(
          'Which users can update existing items in the collection.\n\nLearn more about [item actions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#item-actions).'
        )
        .optional(),
      itemRemove: z
        .enum([
          'ANYONE',
          'SITE_MEMBER',
          'SITE_MEMBER_AUTHOR',
          'CMS_EDITOR',
          'PRIVILEGED',
        ])
        .describe(
          'Which users can remove items from the collection.\n\nLearn more about [item actions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#item-actions).'
        )
        .optional(),
      specialPermissions: z
        .array(
          z.intersection(
            z.object({
              _id: z
                .string()
                .describe('ID of the special permissions set.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              itemRead: z
                .enum(['UNSPECIFIED', 'ALLOWED'])
                .describe(
                  'Whether the specified users or roles can read items from the collection.\n\nWhen set to `ALLOWED`, users can read items from the collection. When set to `UNSPECIFIED`, [collection-level permissions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#collection-level-permissions) apply.\n\nLearn more about [item actions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#item-actions).'
                )
                .optional(),
              itemInsert: z
                .enum(['UNSPECIFIED', 'ALLOWED'])
                .describe(
                  'Whether the specified users or roles can create new items in the collection.\n\nWhen set to `ALLOWED`, users can create new items in the collection. When set to `UNSPECIFIED`, [collection-level permissions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#collection-level-permissions) apply.\n\nLearn more about [item actions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#item-actions).'
                )
                .optional(),
              itemUpdate: z
                .enum(['UNSPECIFIED', 'ALLOWED'])
                .describe(
                  'Whether the specified users or roles can update existing items in the collection.\n\nWhen set to `ALLOWED`, users can update existing items in the collection. When set to `UNSPECIFIED`, [collection-level permissions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#collection-level-permissions) apply.\n\nLearn more about [item actions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#item-actions).'
                )
                .optional(),
              itemRemove: z
                .enum(['UNSPECIFIED', 'ALLOWED'])
                .describe(
                  'Whether the specified users or roles can remove items from the collection.\n\nWhen set to `ALLOWED`, users can remove items from the collection. When set to `UNSPECIFIED`, [collection-level permissions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#collection-level-permissions) apply.\n\nLearn more about [item actions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#item-actions).'
                )
                .optional(),
            }),
            z.xor([
              z.object({
                userId: z.never().optional(),
                policyId: z.never().optional(),
              }),
              z.object({
                policyId: z.never().optional(),
                userId: z
                  .string()
                  .describe('ID of the user for these special permissions.')
                  .max(64),
              }),
              z.object({
                userId: z.never().optional(),
                policyId: z
                  .string()
                  .describe(
                    'ID of the [role](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#user-roles) for these special permissions.\n\nTo find the ID of a role, call [Get Roles Info](https://dev.wix.com/docs/rest/account-level/user-management/accounts/users/get-roles-info). Roles and their IDs are found in the `predefinedRoles` or the `customRoles` arrays in the method response.\n\nLearn more about [roles and permissions for site management](https://support.wix.com/en/article/roles-permissions-overview#roles-and-permissions).'
                  )
                  .max(64),
              }),
            ])
          )
        )
        .max(1000)
        .optional(),
    })
    .describe('Updated permissions of the specified collection.')
    .optional(),
});
export const AddSpecialPermissionsRequest = /*#__PURE__*/ z.object({
  dataCollectionId: z
    .string()
    .describe('ID of the collection for which to grant special permissions.')
    .max(256),
  specialPermissions: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('ID of the special permissions set.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        itemRead: z.enum(['UNSPECIFIED', 'ALLOWED']).optional(),
        itemInsert: z.enum(['UNSPECIFIED', 'ALLOWED']).optional(),
        itemUpdate: z.enum(['UNSPECIFIED', 'ALLOWED']).optional(),
        itemRemove: z.enum(['UNSPECIFIED', 'ALLOWED']).optional(),
      }),
      z.xor([
        z.object({
          policyId: z.never().optional(),
          userId: z
            .string()
            .describe('ID of the user for these special permissions.')
            .max(64),
        }),
        z.object({
          userId: z.never().optional(),
          policyId: z
            .string()
            .describe(
              'ID of the [role](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#user-roles) for these special permissions.\n\nTo find the ID of a role, call [Get Roles Info](https://dev.wix.com/docs/rest/account-level/user-management/accounts/users/get-roles-info). Roles and their IDs are found in the `predefinedRoles` or the `customRoles` arrays in the method response.\n\nLearn more about [roles and permissions for site management](https://support.wix.com/en/article/roles-permissions-overview#roles-and-permissions).'
            )
            .max(64),
        }),
      ])
    )
    .describe(
      'Special permissions to grant to the user or role for the specified collection.'
    ),
});
export const AddSpecialPermissionsResponse = /*#__PURE__*/ z.object({
  specialPermissions: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('ID of the special permissions set.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        itemRead: z
          .enum(['UNSPECIFIED', 'ALLOWED'])
          .describe(
            'Whether the specified users or roles can read items from the collection.\n\nWhen set to `ALLOWED`, users can read items from the collection. When set to `UNSPECIFIED`, [collection-level permissions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#collection-level-permissions) apply.\n\nLearn more about [item actions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#item-actions).'
          )
          .optional(),
        itemInsert: z
          .enum(['UNSPECIFIED', 'ALLOWED'])
          .describe(
            'Whether the specified users or roles can create new items in the collection.\n\nWhen set to `ALLOWED`, users can create new items in the collection. When set to `UNSPECIFIED`, [collection-level permissions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#collection-level-permissions) apply.\n\nLearn more about [item actions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#item-actions).'
          )
          .optional(),
        itemUpdate: z
          .enum(['UNSPECIFIED', 'ALLOWED'])
          .describe(
            'Whether the specified users or roles can update existing items in the collection.\n\nWhen set to `ALLOWED`, users can update existing items in the collection. When set to `UNSPECIFIED`, [collection-level permissions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#collection-level-permissions) apply.\n\nLearn more about [item actions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#item-actions).'
          )
          .optional(),
        itemRemove: z
          .enum(['UNSPECIFIED', 'ALLOWED'])
          .describe(
            'Whether the specified users or roles can remove items from the collection.\n\nWhen set to `ALLOWED`, users can remove items from the collection. When set to `UNSPECIFIED`, [collection-level permissions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#collection-level-permissions) apply.\n\nLearn more about [item actions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#item-actions).'
          )
          .optional(),
      }),
      z.xor([
        z.object({
          userId: z.never().optional(),
          policyId: z.never().optional(),
        }),
        z.object({
          policyId: z.never().optional(),
          userId: z
            .string()
            .describe('ID of the user for these special permissions.')
            .max(64),
        }),
        z.object({
          userId: z.never().optional(),
          policyId: z
            .string()
            .describe(
              'ID of the [role](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#user-roles) for these special permissions.\n\nTo find the ID of a role, call [Get Roles Info](https://dev.wix.com/docs/rest/account-level/user-management/accounts/users/get-roles-info). Roles and their IDs are found in the `predefinedRoles` or the `customRoles` arrays in the method response.\n\nLearn more about [roles and permissions for site management](https://support.wix.com/en/article/roles-permissions-overview#roles-and-permissions).'
            )
            .max(64),
        }),
      ])
    )
    .describe(
      'Special permissions granted to the user or role for the specified collection.'
    )
    .optional(),
});
export const UpdateSpecialPermissionsRequest = /*#__PURE__*/ z.object({
  _id: z
    .string()
    .describe('ID of the special permissions set.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  specialPermissions: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('ID of the special permissions set.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        itemRead: z.enum(['UNSPECIFIED', 'ALLOWED']).optional(),
        itemInsert: z.enum(['UNSPECIFIED', 'ALLOWED']).optional(),
        itemUpdate: z.enum(['UNSPECIFIED', 'ALLOWED']).optional(),
        itemRemove: z.enum(['UNSPECIFIED', 'ALLOWED']).optional(),
      }),
      z.xor([
        z.object({
          userId: z.never().optional(),
          policyId: z.never().optional(),
        }),
        z.object({
          policyId: z.never().optional(),
          userId: z
            .string()
            .describe('ID of the user for these special permissions.')
            .max(64),
        }),
        z.object({
          userId: z.never().optional(),
          policyId: z
            .string()
            .describe(
              'ID of the [role](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#user-roles) for these special permissions.\n\nTo find the ID of a role, call [Get Roles Info](https://dev.wix.com/docs/rest/account-level/user-management/accounts/users/get-roles-info). Roles and their IDs are found in the `predefinedRoles` or the `customRoles` arrays in the method response.\n\nLearn more about [roles and permissions for site management](https://support.wix.com/en/article/roles-permissions-overview#roles-and-permissions).'
            )
            .max(64),
        }),
      ])
    )
    .describe('Special permissions to update for the specified user or role.'),
});
export const UpdateSpecialPermissionsResponse = /*#__PURE__*/ z.object({
  specialPermissions: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('ID of the special permissions set.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        itemRead: z
          .enum(['UNSPECIFIED', 'ALLOWED'])
          .describe(
            'Whether the specified users or roles can read items from the collection.\n\nWhen set to `ALLOWED`, users can read items from the collection. When set to `UNSPECIFIED`, [collection-level permissions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#collection-level-permissions) apply.\n\nLearn more about [item actions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#item-actions).'
          )
          .optional(),
        itemInsert: z
          .enum(['UNSPECIFIED', 'ALLOWED'])
          .describe(
            'Whether the specified users or roles can create new items in the collection.\n\nWhen set to `ALLOWED`, users can create new items in the collection. When set to `UNSPECIFIED`, [collection-level permissions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#collection-level-permissions) apply.\n\nLearn more about [item actions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#item-actions).'
          )
          .optional(),
        itemUpdate: z
          .enum(['UNSPECIFIED', 'ALLOWED'])
          .describe(
            'Whether the specified users or roles can update existing items in the collection.\n\nWhen set to `ALLOWED`, users can update existing items in the collection. When set to `UNSPECIFIED`, [collection-level permissions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#collection-level-permissions) apply.\n\nLearn more about [item actions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#item-actions).'
          )
          .optional(),
        itemRemove: z
          .enum(['UNSPECIFIED', 'ALLOWED'])
          .describe(
            'Whether the specified users or roles can remove items from the collection.\n\nWhen set to `ALLOWED`, users can remove items from the collection. When set to `UNSPECIFIED`, [collection-level permissions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#collection-level-permissions) apply.\n\nLearn more about [item actions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#item-actions).'
          )
          .optional(),
      }),
      z.xor([
        z.object({
          userId: z.never().optional(),
          policyId: z.never().optional(),
        }),
        z.object({
          policyId: z.never().optional(),
          userId: z
            .string()
            .describe('ID of the user for these special permissions.')
            .max(64),
        }),
        z.object({
          userId: z.never().optional(),
          policyId: z
            .string()
            .describe(
              'ID of the [role](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/introduction#user-roles) for these special permissions.\n\nTo find the ID of a role, call [Get Roles Info](https://dev.wix.com/docs/rest/account-level/user-management/accounts/users/get-roles-info). Roles and their IDs are found in the `predefinedRoles` or the `customRoles` arrays in the method response.\n\nLearn more about [roles and permissions for site management](https://support.wix.com/en/article/roles-permissions-overview#roles-and-permissions).'
            )
            .max(64),
        }),
      ])
    )
    .describe('Special permissions updated for the specified user or role.')
    .optional(),
});
export const RemoveSpecialPermissionsRequest = /*#__PURE__*/ z.object({
  specialPermissionsId: z
    .string()
    .describe('ID of the special permissions to remove.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const RemoveSpecialPermissionsResponse = /*#__PURE__*/ z.object({});
export const GetMyPermissionsRequest = /*#__PURE__*/ z.object({
  dataCollectionId: z
    .string()
    .describe(
      "ID of the collection for which to check the current user's permissions."
    )
    .max(256),
});
export const GetMyPermissionsResponse = /*#__PURE__*/ z.object({
  itemRead: z
    .boolean()
    .describe('Whether the current user can read items from the collection.')
    .optional(),
  itemInsert: z
    .boolean()
    .describe(
      'Whether the current user can create new items in the collection.'
    )
    .optional(),
  itemUpdate: z
    .boolean()
    .describe(
      'Whether the current user can update existing items in the collection.'
    )
    .optional(),
  itemRemove: z
    .boolean()
    .describe('Whether the current user can remove items from the collection.')
    .optional(),
});
